import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import entete from "../../../assets/img/entete_pool.png";
import { formatIttValueForDisplay, frenchDate, frenchDateOnly, getIttUnitLabel } from "../../../variables/functions";
import "../../../assets/styles/offre.css";
import axiosInstance from "../../../variables/api/axiosInstance";
import { findPretiumLabel, mapPretiumDolorisOptions, selectApplicableSmig } from "../../../utils/paramHelpers";
import { displayNumbers } from "../../../variables/numbersFunctions";
import { DocumentFooter } from "../../../components/docs/DocumentFooter";
import { getCompagnieLogo } from "../../../hooks/useFilesHooks";


const PresentationOffre = React.forwardRef((props, ref) => {
    const tauxResponsabilite = props?.sinistre?.tauxResponsabilite || 100.00;
    const [smig, setSmig] = useState(52500);
    const [pretiumDolorisOptions, setPretiumDolorisOptions] = useState([]);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

    // ========== PARAMÈTRES DE TAILLE - AJUSTEZ ICI ==========
    const FONT_SIZE = {
        base: 14,           // Taille de police principale
        small: 12,           // Petits textes
        title: 15,          // Titre principal
        sectionTitle: 14,   // Titres de sections
        tableHeader: 13,    // En-têtes de tableau
        tableBody: 12,       // Corps de tableau
    };

    const SPACING = {
        sectionMargin: 8,   // Espacement entre sections (réduit)
        padding: "15px 25px", // Padding du document
    };
    // ========================================================


    useEffect(() => {
        if (props?.sinistre?.contrat?.organisme?.typeOrganisme !== "POOL") {
            getCompagnieLogo(props?.sinistre?.contrat?.organisme?.code)
                .then(async (response) => {
                    const imageBytes = await response.arrayBuffer();
                    var blob = new Blob([imageBytes], { type: "image/png" });
                    var imageUrl = URL.createObjectURL(blob);
                    setLogoPreviewUrl(imageUrl);
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    }, []);

    useEffect(() => {
        let isMounted = true;
        axiosInstance
            .get(`/api/pretium-doloris`)
            .then((res) => {
                if (!isMounted) {
                    return;
                }
                setPretiumDolorisOptions(mapPretiumDolorisOptions(res.data));
            })
            .catch((error) => {
                console.error('Erreur lors du chargement des prétium doloris:', error);
                if (isMounted) {
                    setPretiumDolorisOptions([]);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        axiosInstance
            .get(`/api/smig`)
            .then((res) => {
                if (!isMounted) {
                    return;
                }
                const value = selectApplicableSmig(res.data, props?.sinistre);
                if (value) {
                    setSmig(value);
                }
            })
            .catch((error) => {
                console.error('Erreur lors du chargement du SMIG:', error);
            });

        return () => {
            isMounted = false;
        };
    }, [props?.sinistre]);

    const expertisePretiumLabel = useMemo(
        () => findPretiumLabel(pretiumDolorisOptions, props?.expertise?.pretiumDoloris),
        [pretiumDolorisOptions, props?.expertise?.pretiumDoloris]
    );

    const expertisePEsthetiqueLabel = useMemo(
        () => findPretiumLabel(pretiumDolorisOptions, props?.expertise?.prejudiceEsthetique),
        [pretiumDolorisOptions, props?.expertise?.prejudiceEsthetique]
    );
    return (
        <div ref={ref} className="container-fluid printable-document my-1">
            {/* En-tête */}
            <div style={{ marginBottom: `${SPACING.sectionMargin}px` }}>
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "10px"
                }}>
                    {props?.sinistre?.contrat?.organisme?.typeOrganisme !== "POOL" && logoPreviewUrl && (
                        <img
                            src={logoPreviewUrl}
                            style={{ width: "auto", height: "50px", objectFit: "contain" }}
                            alt="Logo"
                        />
                    )}
                    <img
                        src={entete}
                        style={{ width: "auto", height: "50px", objectFit: "contain" }}
                        alt="En-tête"
                    />
                </div>
                <div style={{ height: "2px", background: "linear-gradient(to right, #2563eb, #3b82f6)", marginTop: "5px" }} />
            </div>

            {/* Titre */}
            <h4 class="text-center my-1 data-value">
                Présentation de l'offre
            </h4>

            {/* Informations victime */}
            <div class="info-section mb-1">
                <h6 class="fw-bold data-value">
                    Informations sur la victime
                </h6>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "5px", fontSize: `${FONT_SIZE.small}px` }}>
                    <div>Nom : <span class="data-value">{props?.offre?.victime?.personne?.nom}</span></div>
                    <div>Prénom : <span class="data-value">{props?.offre?.victime?.personne?.prenom}</span></div>
                    <div>Profession : <span class="data-value">{props?.offre?.victime?.personne?.profession}</span></div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "5px", fontSize: `${FONT_SIZE.small}px`, marginTop: "3px" }}>
                    <div>Sexe : <span class="data-value">{props?.offre?.victime?.personne?.genre === "MASCULIN" ? "M" : "F"}</span></div>
                    <div>Date de naissance : <span class="data-value">{props?.offre?.victime?.personne?.dateNaissance ? frenchDateOnly(props?.offre?.victime?.personne?.dateNaissance) : "-"}</span></div>
                    <div>Date de consolidation : <span class="data-value">{props?.offre?.victime?.dateConsolidation ? frenchDateOnly(props?.offre?.victime?.dateConsolidation) : '-'}</span></div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "5px", fontSize: `${FONT_SIZE.small}px`, marginTop: "3px" }}>
                    <div>Revenu : <span class="data-value">{props?.offre?.victime?.revenuProfessionelMensuelAvantAccident ? `${displayNumbers(props?.offre?.victime?.revenuProfessionelMensuelAvantAccident)} FCFA` : '-'}</span></div>
                    <div>SMIG : <span class="data-value">{smig ? `${displayNumbers(smig)} FCFA` : '-'}</span></div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "5px", fontSize: `${FONT_SIZE.small}px`, marginTop: "3px" }}>
                    <div>N° sinistre : <span class="data-value">{props?.sinistre?.numSinistre || props?.sinistre?.numeroSinistreM}</span></div>
                    <div>Survenu le : <span class="data-value">{props?.sinistre?.dateSinistre ? frenchDate(props?.sinistre?.dateSinistre) : '-'}</span></div>
                    <div>N° Police : <span class="data-value">{props?.sinistre?.contrat?.numeroPoliceM || props?.sinistre?.contrat?.numeroPoliceP}</span></div>
                </div>
            </div>

            {/* Rapport d'expertise */}
            <div class="info-section mb-1">
                <h6 class="fw-bold data-value">
                    Information du rapport d'expertise médicale
                </h6>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "5px", fontSize: `${FONT_SIZE.small}px` }}>
                    <div>ITT en nombre de {getIttUnitLabel(props?.expertise?.ittUnit).toLowerCase()} : <span class="data-value">{formatIttValueForDisplay(props?.expertise?.itt, props?.expertise?.ittUnit)}</span></div>
                    <div>Taux d'IPP : <span class="data-value">{props?.expertise?.ipp}</span></div>
                    <div>Pretium doloris : <span class="data-value">{expertisePretiumLabel ? `${expertisePretiumLabel} (${props?.expertise?.pretiumDoloris}%)` : '-'}</span></div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "5px", fontSize: `${FONT_SIZE.small}px`, marginTop: "3px" }}>
                    <div>Préjudice esthétique : <span class="data-value">{expertisePEsthetiqueLabel ? `${expertisePEsthetiqueLabel} (${props?.expertise?.prejudiceEsthetique}%)` : '-'}</span></div>
                    <div>Besoin d'assistance : <span class="data-value">{props?.expertise?.besoinAssistance ? "OUI" : "NON"}</span></div>
                    <div>Préjudice de carrière : <span class="data-value">{props?.expertise?.prejudiceCarriere ? "OUI" : "NON"}</span></div>
                </div>
            </div>

            {/* Offre */}
            <div class="info-section mb-1">
                <h6 class="fw-bold data-value">
                    Offre
                </h6>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "5px", fontSize: `${FONT_SIZE.small}px` }}>
                    Taux de responsabilité : <span class="data-value">{props?.sinistre?.tauxResponsabilite ? `${props?.sinistre?.tauxResponsabilite}%` : '100%'}</span>
                </div>
            </div>

            {/* Tableau */}
            <div class="table-responsive table-container mb-1">
                <table class="table table-bordered table-sm table-custom">
                    <thead class="bg-light text-center">
                        <tr>
                            <th rowspan="2" class="align-middle">PREJUDICES INDEMNISABLES</th>
                            <th colspan="2">MONTANT</th>
                        </tr>
                        <tr>
                            <th>MONTANT SANS PARTAGE <br /> DE RESPONSABILITE</th>
                            <th>MONTANT APRES PARTAGE <br /> DE RESPONSABILITE</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>FRAIS MEDICAUX JUSTIFIES ET AUTRES FRAIS (Art 258 du code CIMA):</td>
                            <td>{props?.offre?.montantFraisMedicaux ? displayNumbers(props?.offre?.montantFraisMedicaux) : '0'}</td>
                            <td>{props?.offre?.montantFraisMedicaux ? displayNumbers(props?.offre?.montantFraisMedicaux * tauxResponsabilite / 100) : '0'}</td>
                        </tr>
                        <tr>
                            <td>INCAPACITE TEMPORAIRE</td>
                            <td>{props?.offre?.montantItt ? displayNumbers(props?.offre?.montantItt) : '0'}</td>
                            <td>{props?.offre?.montantItt ? displayNumbers(props?.offre?.montantItt * tauxResponsabilite / 100) : '0'}</td>
                        </tr>
                        <tr>
                            <td colspan="3" class="fw-bold bg-light">INCAPACITE PERMANENTE</td>
                        </tr>
                        <tr>
                            <td class="indent-cell">Préjudice physiologique</td>
                            <td>{props?.offre?.montantPrejudicePhysiologique ? displayNumbers(props?.offre?.montantPrejudicePhysiologique) : '0'}</td>
                            <td>{props?.offre?.montantPrejudicePhysiologique ? displayNumbers(props?.offre?.montantPrejudicePhysiologique * tauxResponsabilite / 100) : '0'}</td>
                        </tr>
                        <tr>
                            <td class="indent-cell">Préjudice économique</td>
                            <td>{props?.offre?.montantPrejudiceEconomique ? displayNumbers(props?.offre?.montantPrejudiceEconomique) : '0'}</td>
                            <td>{props?.offre?.montantPrejudiceEconomique ? displayNumbers(props?.offre?.montantPrejudiceEconomique * tauxResponsabilite / 100) : '0'}</td>
                        </tr>
                        <tr>
                            <td class="indent-cell">Préjudice moral</td>
                            <td>{props?.offre?.montantPrejudiceMoral ? displayNumbers(props?.offre?.montantPrejudiceMoral) : '0'}</td>
                            <td>{props?.offre?.montantPrejudiceMoral ? displayNumbers(props?.offre?.montantPrejudiceMoral * tauxResponsabilite / 100) : '0'}</td>
                        </tr>
                        <tr>
                            <td class="fw-bold bg-light">TOTAL INCAPACITE PERMANENTE (Art 260 du code CIMA):</td>
                            <td>{props?.offre?.montantIpp ? displayNumbers(props?.offre?.montantIpp) : '0'}</td>
                            <td>{props?.offre?.montantIpp ? displayNumbers(props?.offre?.montantIpp * tauxResponsabilite / 100) : '0'}</td>
                        </tr>
                        <tr>
                            <td>ASSISTANCE D'UNE TIERCE PERSONNE (Art 261 du code CIMA):</td>
                            <td>{props?.offre?.montantAssistanceTiercePersonne ? displayNumbers(props?.offre?.montantAssistanceTiercePersonne) : '0'}</td>
                            <td>{props?.offre?.montantAssistanceTiercePersonne ? displayNumbers(props?.offre?.montantAssistanceTiercePersonne * tauxResponsabilite / 100) : '0'}</td>
                        </tr>
                        <tr>
                            <td>PRETIUM DOLORIS (Art 262 du code CIMA):</td>
                            <td>{props?.offre?.montantPretiumDoloris ? displayNumbers(props?.offre?.montantPretiumDoloris) : '0'}</td>
                            <td>{props?.offre?.montantPretiumDoloris ? displayNumbers(props?.offre?.montantPretiumDoloris * tauxResponsabilite / 100) : '0'}</td>
                        </tr>
                        <tr>
                            <td>PREJUDICE ESTHETIQUE (Art 262 du code CIMA):</td>
                            <td>{props?.offre?.montantPrejudiceEsthetique ? displayNumbers(props?.offre?.montantPrejudiceEsthetique) : '0'}</td>
                            <td>{props?.offre?.montantPrejudiceEsthetique ? displayNumbers(props?.offre?.montantPrejudiceEsthetique * tauxResponsabilite / 100) : '0'}</td>
                        </tr>
                        <tr>
                            <td>PREJUDICE SCOLAIRE (Art 263 du code CIMA):</td>
                            <td>{props?.offre?.montantPrejudiceScolaire ? displayNumbers(props?.offre?.montantPrejudiceScolaire) : '0'}</td>
                            <td>{props?.offre?.montantPrejudiceScolaire ? displayNumbers(props?.offre?.montantPrejudiceScolaire * tauxResponsabilite / 100) : '0'}</td>
                        </tr>
                        <tr>
                            <td>PREJUDICE DE CARRIERE (Art 263 du code CIMA):</td>
                            <td>{props?.offre?.montantPrejudiceCarriere ? displayNumbers(props?.offre?.montantPrejudiceCarriere) : '0'}</td>
                            <td>{props?.offre?.montantPrejudiceCarriere ? displayNumbers(props?.offre?.montantPrejudiceCarriere * tauxResponsabilite / 100) : '0'}</td>
                        </tr>
                        <tr>
                            <td>PREJUDICE DES LESES A LA CHARGE EFFECTIVE DE LA VICTIME</td>
                            <td>{props?.offre?.montantPrejudiceLeses ? displayNumbers(props?.offre?.montantPrejudiceLeses) : '0'}</td>
                            <td>{props?.offre?.montantPrejudiceLeses ? displayNumbers(props?.offre?.montantPrejudiceLeses * tauxResponsabilite / 100) : '0'}</td>
                        </tr>
                        <tr class="fw-bold bg-light">
                            <td>TOTAL DES INDEMNITES</td>
                            <td>{props?.offre?.totalIndemnites ? displayNumbers(props?.offre?.totalIndemnites) : '0'}</td>
                            <td>{props?.offre?.totalIndemnites ? displayNumbers(props?.offre?.totalIndemnites * tauxResponsabilite / 100) : '0'}</td>
                        </tr>
                        <tr class="fw-bold bg-light">
                            <td>PROVISIONS A DEDUIRE (-)</td>
                            <td>{props?.offre?.provisions ? displayNumbers(props?.offre?.provisions) : '0'}</td>
                            <td></td>
                        </tr>
                        <tr class="fw-bold bg-dark text-white">
                            <td colspan="2" class="text-center text-white">TOTAL A PAYER</td>
                            <td className="text-white">{props?.offre?.totalAPayer ? displayNumbers(props?.offre?.totalAPayer) : '0'}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Signature */}
            <div style={{ marginTop: "15px", textAlign: "center", fontSize: `${FONT_SIZE.small}px` }}>
                <p style={{ margin: "5px 0" }}>Fait à Lomé le ...../...../.....</p>
                <br />
                <p style={{ margin: "5px 0" }}>Pour la Société</p>
                <br />
            </div>
            {/* <footer> */}
            <DocumentFooter />
            {/* </footer> */}
        </div>
    );
});

PresentationOffre.propTypes = {
    offre: PropTypes.object,
    sinistre: PropTypes.object,
};

export default PresentationOffre;
